import { useQuery } from "@apollo/client/react";
import { PlusIcon } from "lucide-react";
import { useMemo } from "react";

import type {
  DocumentTemplateFragment,
  EntityFragment,
} from "@/__generated__/graphql";
import { useOpenRecord } from "@/app/app/_/routing";
import { SelectableCardList } from "@/components/molecules/SelectableCardList";
import {
  SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT,
  SelectableListItemRow,
  SelectableListItemRowOverline,
  SelectableListItemRowTitleEnd,
} from "@/components/molecules/SelectableListItemRow";
import { Queries } from "@/constants";
import { DocumentTemplateObject } from "@/objects/DocumentTemplateObject";
import { State } from "@/state";
import { Button } from "@/components/ui/button";

/**
 * A business's invoice templates, its default first: how its invoices look and
 * what they tell their payer. Each opens as its own record; a new one is made
 * in the business. Each new invoice is given the default.
 */
export function EntityTemplatesPanel({ entity }: { entity: EntityFragment }) {
  const openRecord = useOpenRecord();
  const { data, loading } = useQuery(Queries.DOCUMENT_TEMPLATES_QUERY, {
    variables: { entityIdentifiers: [entity.identifier] },
    fetchPolicy: "cache-and-network",
  });
  const templates = useMemo<DocumentTemplateFragment[]>(
    () => data?.documentTemplates?.data ?? [],
    [data],
  );

  return (
    <div
      // A list, on the recessed surface lists sit on.
      className="flex-1 min-h-0 w-full flex flex-col overflow-hidden bg-muted dark:bg-[#151719]"
      data-testid="entity-templates"
    >
      <div className="flex justify-end p-2">
        <Button
          variant="outline"
          size="sm"
          data-testid="entity-templates-new"
          onClick={() =>
            State.getState().pushToCommandStack({
              commandDefinitionId: "create-DocumentTemplateObject",
              context: { entityIdentifier: entity.identifier },
            })
          }
        >
          <PlusIcon />
          New template
        </Button>
      </div>
      <SelectableCardList
        size="sm"
        variant="outlined"
        layout="cards"
        estimatedItemHeight={SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT}
        items={templates}
        loading={loading && templates.length === 0}
        placeholderItem={DocumentTemplateObject.placeholder!}
        getItemKey={(template) => template.id}
        onItemClick={(template) =>
          openRecord({ id: template.id, identifier: template.identifier })
        }
        emptyState={{
          ReactIcon: DocumentTemplateObject.ReactIcon,
          title: "No templates yet",
          description:
            "A template sets how invoices look - layout, font and colour - and how they're paid, such as an e-transfer address.",
        }}
        renderItem={({ item: template, isLoading }) => (
          <SelectableListItemRow
            overline={
              <SelectableListItemRowOverline>
                {[template.layout, template.font]
                  .filter(Boolean)
                  .map((word) => word!.charAt(0) + word!.slice(1).toLowerCase())
                  .join(" · ")}
              </SelectableListItemRowOverline>
            }
            overlineEnd={
              template.isDefault ? (
                <SelectableListItemRowTitleEnd>
                  Default
                </SelectableListItemRowTitleEnd>
              ) : null
            }
            title={template.name ?? ""}
            description={template.paymentInstructions || null}
            isLoading={isLoading}
            rootDataAttrs={{
              "data-testid": "entity-template",
              "data-template-id": template.identifier,
            }}
          />
        )}
      />
    </div>
  );
}
